import type { FocusRepair, ToastTone } from '@akpl/ui-tv';
import type { ReactNode } from 'react';
import type { StateCreator } from 'zustand';

import type { TvStore } from './types';

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  ttlMs: number;
}

export interface DialogAction {
  key: string;
  label: string;
  primary?: boolean;
  onSelect: () => void;
}

export interface DialogSpec {
  id: string;
  title: string;
  /** Plain text, or any node (the Queue puts the join QR here). */
  body?: ReactNode;
  actions: DialogAction[];
  /** Back key / explicit close. Defaults to `closeDialog`. */
  onClose?: () => void;
}

export interface FocusRepairEntry extends FocusRepair {
  at: number;
}

export interface UiState {
  ui: {
    toasts: ToastItem[];
    dialog: DialogSpec | null;
    /** RTDB `.info/connected` (true until told otherwise). */
    connected: boolean;
    /** Banner after > 5 s disconnected (docs/04 §5). */
    reconnecting: boolean;
    /**
     * Focus repairs the watchdog had to make (docs/04 §2.1, QA3 T1): the last few for the dev
     * overlay, and a total so a run that keeps limping is visible at a glance.
     */
    focusRepairs: FocusRepairEntry[];
    focusRepairCount: number;
  };
}

export interface UiActions {
  showToast(message: string, tone?: ToastTone, ttlMs?: number): number;
  dismissToast(id: number): void;
  openDialog(spec: DialogSpec): void;
  closeDialog(): void;
  setConnected(connected: boolean): void;
  setReconnecting(reconnecting: boolean): void;
  noteFocusRepair(repair: FocusRepair): void;
}

export type UiSlice = UiState & UiActions;

export const TOAST_TTL_MS = 3000;
/** How many repairs the dev overlay keeps around. */
export const FOCUS_REPAIR_HISTORY = 5;
let toastSeq = 0;

export const createUiSlice: StateCreator<TvStore, [], [], UiSlice> = (set) => ({
  ui: {
    toasts: [],
    dialog: null,
    connected: true,
    reconnecting: false,
    focusRepairs: [],
    focusRepairCount: 0,
  },
  showToast: (message, tone = 'info', ttlMs = TOAST_TTL_MS) => {
    toastSeq += 1;
    const id = toastSeq;
    set((s) => ({
      ui: { ...s.ui, toasts: [...s.ui.toasts.slice(-2), { id, message, tone, ttlMs }] },
    }));
    return id;
  },
  dismissToast: (id) =>
    set((s) => ({ ui: { ...s.ui, toasts: s.ui.toasts.filter((t) => t.id !== id) } })),
  openDialog: (dialog) => set((s) => ({ ui: { ...s.ui, dialog } })),
  closeDialog: () => set((s) => (s.ui.dialog ? { ui: { ...s.ui, dialog: null } } : {})),
  setConnected: (connected) => set((s) => ({ ui: { ...s.ui, connected } })),
  setReconnecting: (reconnecting) => set((s) => ({ ui: { ...s.ui, reconnecting } })),
  noteFocusRepair: (repair) =>
    set((s) => ({
      ui: {
        ...s.ui,
        focusRepairs: [
          ...s.ui.focusRepairs.slice(-(FOCUS_REPAIR_HISTORY - 1)),
          { ...repair, at: Date.now() },
        ],
        focusRepairCount: s.ui.focusRepairCount + 1,
      },
    })),
});
